<template>
  <iColumn gap="standard" width="fill">
    <iRow gap="small" wrap="wrap">
      <button
        v-for="platform in platforms"
        :key="platform.value"
        type="button"
        class="social-pill"
        :class="{'social-pill-active': isPlatformActive(platform.value)}"
        @click="togglePlatform(platform.value)"
      >
        <iIcon v-if="platform.icon" :name="platform.icon" :size="18" />
        <span class="social-pill-label">{{ platform.label }}</span>
      </button>
    </iRow>

    <iColumn v-if="activePlatforms.length > 0" gap="standard" width="fill">
      <iRow
        v-for="platform in activePlatforms"
        :key="platform.value"
        gap="standard"
        wrap="nowrap"
        vertical-align="middle"
        width="fill"
      >
        <iColumn :min-width="140" width="hug" gap="extraSmall">
          <iRow gap="extraSmall" vertical-align="middle" wrap="nowrap">
            <iIcon v-if="platform.icon" :name="platform.icon" :size="18" />
            <iText variant="subtle">
              {{ platform.label }}
            </iText>
          </iRow>
        </iColumn>
        <iColumn width="fill">
          <iTextInput
            :model-value="valueFor(platform.value)"
            :placeholder="platform.placeholder || ''"
            width="fill"
            @update:model-value="updatePlatform(platform.value, $event)"
          />
        </iColumn>
      </iRow>
    </iColumn>
  </iColumn>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useQuestionnaireStore } from "@/stores/questionnaire-store";

export default {
  name: "QuestionnaireSocialLinks",
  props: {
    sectionKey: {
      type: String,
      required: true,
    },
    field: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      expandedPlatformKeys: [],
    };
  },
  computed: {
    ...mapState(useQuestionnaireStore, ["responseData"]),
    platforms() {
      return Array.isArray(this.field.social_platforms) ? this.field.social_platforms : [];
    },
    sectionValue() {
      return this.responseData[this.sectionKey][this.field.key] || {};
    },
    activePlatforms() {
      return this.platforms.filter(platform => this.isPlatformActive(platform.value));
    },
  },
  methods: {
    ...mapActions(useQuestionnaireStore, ["setSocialLinkValue"]),
    valueFor(platformKey) {
      return this.sectionValue[platformKey] || "";
    },
    hasValue(platformKey) {
      const current = this.sectionValue[platformKey];
      return typeof current === "string" && current.trim() !== "";
    },
    isPlatformActive(platformKey) {
      return this.expandedPlatformKeys.includes(platformKey) || this.hasValue(platformKey);
    },
    togglePlatform(platformKey) {
      const expandedIndex = this.expandedPlatformKeys.indexOf(platformKey);
      if (expandedIndex >= 0) {
        this.expandedPlatformKeys.splice(expandedIndex, 1);
        if (this.hasValue(platformKey)) {
          this.setSocialLinkValue(this.sectionKey, this.field.key, platformKey, "");
        }
        return;
      }
      this.expandedPlatformKeys.push(platformKey);
    },
    updatePlatform(platformKey, newValue) {
      this.setSocialLinkValue(this.sectionKey, this.field.key, platformKey, newValue);
    },
  },
  styleGuide: () => ({
    brandedColor: { "color.font": "branded" },
    surfaceColor: { "color.background": "subtle" },
    surfaceTextColor: { "color.font": "standard" },
  }),
};
</script>

<style scoped lang="scss">
.social-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid v-bind('$getStyles.surfaceColor');
    border-radius: 8px;
    background-color: v-bind('$getStyles.surfaceColor');
    color: v-bind('$getStyles.surfaceTextColor');
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
        border-color: v-bind('$getStyles.brandedColor');
    }
}

.social-pill-active {
    background-color: v-bind('$getStyles.brandedColor');
    border-color: v-bind('$getStyles.brandedColor');
    color: #fff;
}

// iIcon paints its own fill/color from its variant, so it stays dark on the
// branded background unless we force it white while the pill is selected.
.social-pill-active :deep(.i-icon),
.social-pill-active :deep(svg) {
    color: #fff;
    fill: #fff;
}

.social-pill-label {
    white-space: nowrap;
}
</style>
